// DTO de "Personas" del directivo (rediseño 2026-07, pantalla nueva).
//
//   - GET /api/directivo/personas?desde&hasta[&planta_id][&turno][&area_id]
//
// Responde "¿cómo está mi gente?" con encuadre de reconocimiento (brief §4.7):
//   - Cobertura de captura: bloques capturados / bloques esperados. El
//     denominador sale de programacion_lideres × horas del turno; el día
//     operativo en curso cuenta solo las horas ya cerradas al corte.
//   - Puntualidad: mediana de minutos entre el CIERRE del bloque y la captura
//     (created_at), topada en 0 por abajo (capturar antes del cierre no es
//     "adelanto", es puntualidad perfecta).
//   - "Dónde apoyar" trae HECHOS calculables (señales), nunca hipótesis
//     causales — la interpretación es del directivo (decisión 2026-07-05).
//   - Reconocimiento: lo deriva el FE de las mismas filas (una sola verdad).
import { z } from 'zod';
export const directivoPersonasQuerySchema = z
    .object({
    desde: z.string().date(),
    hasta: z.string().date(),
    planta_id: z.string().uuid().optional(),
    turno: z.coerce.number().int().min(1).max(3).optional(),
    area_id: z.string().uuid().optional(),
})
    .refine((q) => q.desde <= q.hasta, { message: 'desde debe ser <= hasta' });
/** Fila por LÍDER con actividad o asignación en el rango. */
export const personaLiderSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    planta_id: z.string().uuid().nullable(),
    planta_codigo: z.string().nullable(),
    /** Códigos de línea donde estuvo programado o capturó (ordenados). */
    lineas: z.array(z.string()),
    /** Números de turno (1..3) donde estuvo programado o capturó. */
    turnos: z.array(z.number().int()),
    /** Bloques-hora esperados según programacion_lideres (0 si no fue programado). */
    bloques_esperados: z.number(),
    /** Bloques capturados (todos los estados: un invalidado también fue capturado). */
    bloques_capturados: z.number().int(),
    /** capturados / esperados * 100 — null si esperados = 0 (sin programación no se juzga). */
    cobertura_pct: z.number().nullable(),
    /** Mediana de min tras el cierre de cada bloque. null sin capturas. */
    puntualidad_mediana_min: z.number().nullable(),
    /** % de sus bloques con validado_at. null sin capturas. */
    pct_validado: z.number().min(0).max(100).nullable(),
    /** % de sus bloques invalidados. null sin capturas. */
    pct_invalidado: z.number().min(0).max(100).nullable(),
    fuera_turno: z.number().int(),
    /** Cumplimiento vs CAPACIDAD de sus bloques válidos (WS-4: por línea no hay programa). */
    cumplimiento_meta_pct: z.number().nullable(),
    // ── Operación del período (bloques VÁLIDOS, fórmulas de rentabilidad) ──
    /** Unidades producidas. */
    unidades: z.number(),
    /** Capacidad estándar acumulada de sus horas (meta capacidad instalada). */
    meta_capacidad_unds: z.number(),
    /** Horas laboradas (SUM duracion_minutos / 60). */
    horas: z.number(),
    /** Promedio de operarios con los que trabajó. null sin dato. */
    personal_promedio: z.number().nullable(),
    /** SKUs sin precio vigente en sus capturas (el $ es parcial si > 0). */
    skus_sin_precio: z.number().int(),
    // ── $ (null si !puede_ver_costos) ──
    valor_cop: z.number().nullable(),
    /** valor + insumos cobrados (tiendas) − costo MO. */
    utilidad_cop: z.number().nullable(),
});
/** Fila por SUPERVISOR con programación o acciones en el rango. */
export const personaSupervisorSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    /** Códigos de planta que cubrió (puede rotar). */
    planta_codigos: z.array(z.string()),
    turnos: z.array(z.number().int()),
    dias_programados: z.number().int(),
    validaciones: z.number().int(),
    invalidaciones: z.number().int(),
    capturas_delegadas: z.number().int(),
    paros_cerrados: z.number().int(),
    /** Mediana de min entre captura y validación de lo que validó. null si no validó. */
    latencia_mediana_min: z.number().nullable(),
    /**
     * Promedio de min entre captura y validación — MISMA definición que
     * `oportunidad_min` del coordinador (analisis-personal). null si no validó.
     */
    oportunidad_promedio_min: z.number().nullable(),
    /**
     * Bloques válidos SIN validar de los turnos que cubre (programación ∩ plantas
     * que supervisa) — A6 del diccionario, igual que el coordinador.
     */
    pendientes_validacion: z.number().int().min(0),
    /** Paquetes (cantidad_procesada) de esos bloques pendientes. */
    pendientes_paq: z.number().min(0),
    /**
     * Cumplimiento vs capacidad de la producción de sus turnos. Semántica del
     * coordinador: solo bloques con capacidad estándar (skus_lineas) entran al
     * numerador y al denominador.
     */
    cumplimiento_meta_pct: z.number().nullable(),
    // ── Operación de SUS turnos (ronda 2: misma información que el líder) ──
    unidades: z.number(),
    meta_capacidad_unds: z.number(),
    horas: z.number(),
    personal_promedio: z.number().nullable(),
    skus_sin_precio: z.number().int(),
    valor_cop: z.number().nullable(),
    utilidad_cop: z.number().nullable(),
});
export const coberturaPlantaSchema = z.object({
    planta_id: z.string().uuid(),
    codigo: z.string(),
    esperados: z.number(),
    capturados: z.number().int(),
    pct: z.number().nullable(),
});
/** Señales de "dónde apoyar": SITUACIONES medibles, no personas ni hipótesis. */
export const personasSenalesSchema = z.object({
    /** Capturas fuera de turno agrupadas por turno del bloque. */
    fuera_turno_por_turno: z.array(z.object({ turno: z.number().int(), cantidad: z.number().int() })),
    /** Top líneas por invalidaciones en el rango (máx 3) + total. */
    invalidaciones_total: z.number().int(),
    invalidaciones_por_linea: z.array(z.object({
        linea_id: z.string().uuid(),
        linea_codigo: z.string(),
        planta_codigo: z.string(),
        cantidad: z.number().int(),
    })),
    /** Días-línea con programa vigente pero SIN ningún líder asignado ese día. */
    programa_sin_lider: z.array(z.object({
        planta_id: z.string().uuid(),
        planta_codigo: z.string(),
        dias_linea: z.number().int(),
        /** Muestra de códigos de línea afectados (máx 3). */
        lineas: z.array(z.string()),
    })),
});
export const directivoPersonasSchema = z.object({
    rango: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    planta_id: z.string().uuid().nullable(),
    turno: z.number().int().nullable(),
    area_id: z.string().uuid().nullable(),
    puede_ver_costos: z.boolean(),
    plantas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    areas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), planta_id: z.string().uuid() })),
    kpis: z.object({
        cobertura: z.object({
            esperados: z.number(),
            capturados: z.number().int(),
            pct: z.number().nullable(),
            por_planta: z.array(coberturaPlantaSchema),
        }),
        /** Mediana GLOBAL (sobre todos los bloques, no promedio de medianas). */
        puntualidad_mediana_min: z.number().nullable(),
        validacion: z.object({
            total: z.number().int(),
            validados: z.number().int(),
            pct: z.number().nullable(),
            pendientes: z.number().int(),
        }),
        disciplina: z.object({
            fuera_turno: z.number().int(),
            delegadas: z.number().int(),
            bloques_total: z.number().int(),
            /** (fuera_turno + delegadas) / bloques_total * 100. null si no hay bloques. */
            pct_bloques: z.number().nullable(),
        }),
    }),
    lideres: z.array(personaLiderSchema),
    supervisores: z.array(personaSupervisorSchema),
    senales: personasSenalesSchema,
    /**
     * DN10: valor_cop / utilidad_cop por persona van sobre producción VALIDADA
     * (unidades, cobertura, validaciones y pendientes siguen sobre lo capturado).
     */
    base_dinero: z.literal('validada').optional(),
    actualizado_at: z.string(),
});
//# sourceMappingURL=directivo_personas.js.map